import { useState, useRef, useEffect } from 'react';
import useSWR from 'swr';
import { localAxios } from '../api.js';
import ScreenHeader from '../components/ScreenHeader.js';

export default function Reports() {
    const [selectedSiteId, setSelectedSiteId] = useState('');
    const [atTop,    setAtTop]    = useState(true);
    const [atBottom, setAtBottom] = useState(false);
    const tbodyWrapRef = useRef(null);

    const { data: locationsData, isLoading: locationsLoading } = useSWR('/api/locations', localAxios);
    const locations = locationsData?.data ?? [];

    const { data: recordsData, isLoading: recordsLoading, error: recordsError } = useSWR('/api/records', localAxios);
    const allRecords = recordsData?.data ?? [];

    const siteNameMap = Object.fromEntries(locations.map(l => [l.id, l.location_name]));

    const records = selectedSiteId === ''
        ? allRecords
        : allRecords.filter(r => r.site_id === Number(selectedSiteId));

    useEffect(() => {
        const el = tbodyWrapRef.current;
        if (!el) return;
        const check = () => {
            setAtTop(el.scrollTop <= 1);
            setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
        };
        check();
        el.addEventListener('scroll', check);
        return () => el.removeEventListener('scroll', check);
    }, [records.length]);

    const formatDate = (dt) => {
        if (!dt) return '—';
        return new Date(dt).toLocaleDateString('en-US', {
            year: '2-digit', month: 'numeric', day: 'numeric',
        });
    };

    const maskImage = (atTop && atBottom)
        ? 'none'
        : atTop
            ? 'linear-gradient(to bottom, black 70%, transparent 100%)'
            : atBottom
                ? 'linear-gradient(to bottom, transparent 0%, black 30%)'
                : 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)';

    const colGroup = (
        <colgroup>
            <col className="reports-col--name" />
            <col className="reports-col--org" />
            <col className="reports-col--date" />
            <col className="reports-col--site" />
        </colgroup>
    );

    return (
        <div
            className="app-wide__container"
            style={{ height: 'calc(100vh - 2rem)', gridTemplateRows: '60px auto 1fr 70px' }}
        >
            <ScreenHeader message="Reports" />

            <div className="app-wide__add-new">
                <div className="visitor-controls">
                    <div className="visitor-controls__row">
                        <label className="visitor-controls__label">Location</label>
                        <select
                            className="visitor-controls__select"
                            value={selectedSiteId}
                            onChange={e => setSelectedSiteId(e.target.value)}
                            disabled={locationsLoading}
                        >
                            <option value="">All Sites</option>
                            {locations.map(loc => (
                                <option key={loc.id} value={loc.id}>{loc.location_name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="app-wide__main" style={{ overflow: 'hidden' }}>
                {recordsLoading && (
                    <p className="reports-status">Loading records…</p>
                )}
                {recordsError && (
                    <p className="reports-status reports-status--error">Could not load records.</p>
                )}
                {!recordsLoading && !recordsError && (
                    <div className="reports-table-wrap">
                        <table className="reports-table">
                            {colGroup}
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Organization</th>
                                    <th>Date</th>
                                    <th>Site</th>
                                </tr>
                            </thead>
                        </table>
                        <div
                            ref={tbodyWrapRef}
                            className="reports-tbody-wrap"
                            style={{ maskImage, WebkitMaskImage: maskImage }}
                        >
                            <table className="reports-table">
                                {colGroup}
                                <tbody>
                                    {records.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="reports-table__empty">
                                                No records found.
                                            </td>
                                        </tr>
                                    ) : records.map(r => (
                                        <tr key={r.id}>
                                            <td>{r.first_name} {r.last_name}</td>
                                            <td>{r.organization ?? '—'}</td>
                                            <td>{formatDate(r.recorded_datetime)}</td>
                                            <td>{siteNameMap[r.site_id] ?? `Site ${r.site_id}`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <div className="app-wide__footer" />
        </div>
    );
}
